import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
  Badge,
  Box,
  BoxProps,
  darken,
  Dialog,
  Typography,
  useTheme,
} from "@mui/material";
import { Spacer, Toolbar, useDialog } from "common";
import {
  EditMode,
  useEventEditorContext,
} from "features/events/Editor/EventEditorContext";
import { EventItem, EventLimit } from "features/events/eventApi";
import EventForm from "features/events/EventForm";
import { useEventCalculator } from "../../useEventCalculator";

export interface StartBoatCellViewMonthProps extends Omit<BoxProps, "onClick"> {
  item: EventItem;
  boatLimit?: EventLimit;
  isSelected?: boolean;
  onClick?: (item: EventItem) => void;
  onAddClick?: (item: EventItem) => void;
}

export default function EventEditCard({
  item,
  boatLimit,
  onClick,
  onAddClick,
  isSelected,
  sx,
  ...props
}: StartBoatCellViewMonthProps) {
  const theme = useTheme();
  const {
    cal,
    editMode,
    groupEditMode,
    selectedEvents,
    selectEvent,
    openModal,
    firstSelectedEventProgramId,
    viewMode,
  } = useEventEditorContext();

  // Modes where individual event selection is enabled
  const selectionModes: Exclude<EditMode, null>[] = ["groupEdit"];
  const showSelector = !!editMode && selectionModes.includes(editMode);

  // When in event selection mode, if there is at least one selected event,
  // only allow selecting events with the same program_id.
  let disableSelection = false;
  let cardSx = {};
  if (showSelector && selectedEvents.length > 0) {
    if (
      firstSelectedEventProgramId !== null &&
      item.program.id !== firstSelectedEventProgramId
    ) {
      disableSelection = true;
      cardSx = { opacity: 0.3, pointerEvents: "none" };
    }
  }

  // Drag support: enable dragging in dragDropUpdate mode only.
  const dragModes: Exclude<EditMode, null>[] = ["dragDropUpdate"];
  const enableDrag = !!editMode && dragModes.includes(editMode);
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: item.id,
    data: item,
    disabled: !enableDrag,
  });
  const style = {
    transform: enableDrag ? CSS.Translate.toString(transform) : undefined,
    zIndex: enableDrag && transform ? 40000 : undefined,
  };

  const [event, setEvent, eventProps] = useDialog<number>();
  const { getEventData } = useEventCalculator();
  const {
    eventLimit,
    programColor,
    programTextColor,
    programBorderColor,
    limits,
    eventSx,
    eventClassName,
  } = getEventData(item, boatLimit);

  const limitsLabel = limits.map(limit => limit.name?.substring(0, 1)).join("");
  const remainingSpaces =
    (eventLimit?.remaining_places || 0) < 10
      ? eventLimit?.remaining_places
      : "+";
  const label = `${limitsLabel}${
    limits.length > 0 ? "|" : ""
  }${remainingSpaces}`;

  const selected = selectedEvents?.includes(item.id);

  return (
    <Box
      className={`${eventClassName}${isSelected ? " selected" : ""}`}
      onClick={
        editMode === "singleEdit"
          ? evt => {
              evt.stopPropagation();
              console.log("onClick", item.id);
              setEvent(item.id as any);
            }
          : editMode === "groupEdit" &&
            (groupEditMode === "event" || !groupEditMode)
          ? evt => {
              evt.stopPropagation();
              selectEvent(item.id, item.program.id);
            }
          : undefined
      }
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      key={item.id}
      sx={{
        p: 0.5,
        alignSelf: "stretch",
        borderRadius: "4px",
        cursor: "pointer",
        gap: 0.5,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        minHeight: 20,
        minWidth: 20,
        ...(eventSx as any),
        ...sx,
        ...cardSx,
        ...(selected && {
          backgroundImage: `repeating-linear-gradient(
            45deg,
            ${darken(programColor, 0.4)} 0,
            ${darken(programColor, 0.6)} 5px,
            transparent 5px,
            transparent 10px
          )!important`,
        }),
      }}
      {...props}
    >
      {viewMode === "normal" && (
        <>
          <Toolbar
            sx={{ gap: 0.5, overflow: "hidden", textOverflow: "ellipsis" }}
          >
            <Typography
              variant="button"
              sx={{
                maxWidth: `calc(100% - 27px)`,
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.program.code}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: 500, flex: 1, textAlign: "right" }}
            >
              {eventLimit?.reserved_places}/{eventLimit?.number_of_places}
            </Typography>
            <Spacer right />
          </Toolbar>
          <Typography variant="caption" sx={{ fontWeight: 500, flex: 1 }}>
            €: {item.variants?.map(variant => variant.adult_price).join(", ")}
          </Typography>
        </>
      )}
      {!!item.number_of_should_notify && (
        <Badge
          sx={{
            position: "absolute",
            bottom: 6,
            right: 10,
            "& .MuiBadge-badge": {
              boxShadow: theme =>
                `inset 0 0 0 1px ${theme.palette.text.primary}!important`,
            },
          }}
          color="info"
          badgeContent={item.number_of_should_notify}
        />
      )}
      <Dialog {...eventProps} maxWidth="xl" fullWidth>
        <EventForm
          id={item.id as any}
          //defaultValues={item}
          onClose={eventProps.onClose}
          afterSave={(newItem, isCreate) => {
            cal?.current.refresh();
            eventProps.onClose && eventProps.onClose({}, "backdropClick");
          }}
          hideBookingList
        />
      </Dialog>
    </Box>
  );
}
